/**
 * v2 API contract of «План выплат» (/payplan): what each person earns this month, per assignment
 * (docs/research/design2-month-ops.md §2, §5.5–5.7, §6.2; decisions-log V12–V14, V20, V22–V24, V96).
 */
import { z } from 'zod';
import type { FxEstimate } from './fxEstimate';
import type { PayFormat } from '../calc/prices';
import { isIsoDate, isPeriod } from '../dates';
import { OFF_MAX_MINUTES } from './planDetail';
import type { PlanDetail } from './planDetail';

/** Hours of one assignment month are capped at 744 h (31 × 24, design PP commit rule). */
export const PAYPLAN_MAX_MINUTES = 744 * 60;

/**
 * Tables of the screen, in order: «Почасовая оплата», «Фикс в месяц», «Фикс под ключ», «Процент от
 * проекта» (V69); the salaries through our entities have their own section.
 */
export const PAYPLAN_FORMATS = ['hourly', 'monthly', 'scope', 'share'] as const;
export type PayPlanFormat = (typeof PAYPLAN_FORMATS)[number];

/** Status of an assignment month (design `personHourStatus` keys). */
export type PayPlanStatus = 'plan' | 'manual' | 'chat' | 'approved' | 'paidout' | 'changedPay';

export const payPlanPeriodSchema = z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ');

export const payPlanQuerySchema = z.object({
  month: payPlanPeriodSchema,
  issuer: z.string().min(1).default('all'),
});
export type PayPlanQuery = z.infer<typeof payPlanQuerySchema>;

export interface PayPlanPay {
  readonly format: PayFormat;
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  /** Shares (V69): × 1 000 000. */
  readonly shareE6: number | null;
  readonly currency: string;
  readonly source: 'assignment';
}

export interface PayPlanMoney {
  readonly currency: string;
  readonly amountMinor: number;
}

/**
 * `work` — an assignment of the month (the carry between months is on «Выплаты» only, V214); `salary` — a project
 * paid by the salary through our entity (V69, V147): read-only, its «Зачтено в ЗП» is the
 * salary it took, nothing is paid directly.
 */
export const PAYPLAN_LINE_KINDS = ['work', 'salary'] as const;
export type PayPlanLineKind = (typeof PAYPLAN_LINE_KINDS)[number];

/** One assignment (sub row). */
export interface PayPlanLine {
  readonly kind: PayPlanLineKind;
  readonly assignmentId: string;
  readonly projectId: string;
  readonly projectName: string;
  /** Start / end inside the month when the assignment does not cover all of it («с 15 сен», «до 14 окт»). */
  readonly startsOn: string | null;
  readonly endsOn: string | null;
  /** null → «нет ставки». */
  readonly pay: PayPlanPay | null;
  readonly currency: string;
  /** The month's plan — the most it can hold (V96, V306). */
  readonly planMinutes: number;
  /** The plan less the person's leaves, the project's days off and the hours not worked (V306): the fact until one is entered. */
  readonly expectedMinutes: number;
  /** Entered hours; null = `expectedMinutes` apply. */
  readonly minutes: number | null;
  /** «Не отработано, ч» of the assignment month (V306); null = none. */
  readonly offMinutes: number | null;
  /** What the plan and the fact are made of (V307); null for a line paid by the billed hours. */
  readonly plan: PlanDetail | null;
  /** monthly (V199): the project's hours used before hours are entered; null = the plan. */
  readonly defaultMinutes: number | null;
  /**
   * «За выставленный час» (V184): the hours billed to the client, read-only — an estimate from the
   * bill hours until the invoice is issued; null minutes while none are planned. null for other formats.
   */
  readonly billed: { readonly minutes: number | null; readonly isEstimate: boolean } | null;
  /**
   * monthly paid by worked days before hours counted (V96, V240): those days as hours of the month's
   * plan, shown read-only in «Факт, ч» when no hours were entered (the import wrote 0); entered hours
   * still show. Hours never fed its frozen amount. null otherwise.
   */
  readonly paidByDaysMinutes: number | null;
  /** monthly: amount × hours / full-month plan (V96), before an override. */
  readonly defaultAmountMinor: number | null;
  /** Entered amount: the scope stage, or a monthly override. */
  readonly amountMinor: number | null;
  /** The month's pay of the sub row; null → «—». */
  readonly totalMinor: number | null;
  /** scope: the assignment total and what earlier months paid. */
  readonly scope: { readonly totalMinor: number; readonly paidBeforeMinor: number } | null;
  /** The line or its person × currency payout is approved, or the payout paid (V22, V70): an edit asks first (V125). */
  readonly locked: boolean;
  /** The line itself is agreed (V70); the payout is approved once every line of it is. */
  readonly approved: boolean;
  /** The person × currency payout of the month is paid: an edit asks first and marks the line (V125). */
  readonly paid: boolean;
  /** Employer taxes of the line (V67): an employee's share of the month's taxes; null when none. */
  readonly taxMinor: number | null;
  /**
   * «Зачтено в ЗП» (V145): the salary through our entity credited against the line, already
   * deducted from `totalMinor`; a line without a rate of its own shows the salary it took (V147).
   * null when the person has no salary credit this month.
   */
  readonly creditMinor: number | null;
  /** The currency of `creditMinor`: the line's, or the salary's for a line without a rate. */
  readonly creditCurrency: string | null;
  readonly status: PayPlanStatus;
  /** The assignment month record (`timesheet`) for the Activity focus; null before the first entry. */
  readonly recordId: string | null;
}

/** One person in one table (parent row). */
export interface PayPlanPerson {
  readonly personId: string;
  readonly name: string;
  readonly lines: readonly PayPlanLine[];
  /** «Итого» per currency: the month's work, without the carry (V214). */
  readonly totals: readonly PayPlanMoney[];
  /** True while an hourly line has no hours or a scope line has no stage: «Итого» shows «—». */
  readonly preliminary: boolean;
  /** Currencies whose payout is approved and not paid yet: «Отменить утверждение» (V59). */
  readonly unapprove: readonly string[];
  /** Currencies of this row's payouts and whether all of them are approved or paid. */
  readonly approve: { readonly currencies: readonly string[]; readonly done: boolean };
}

export interface PayPlanTable {
  readonly format: PayPlanFormat;
  readonly people: readonly PayPlanPerson[];
}

export const SALARY_ENTRY_KINDS = ['advance', 'salary', 'vacation', 'sick', 'bonus', 'compensation', 'unpaid', 'aid', 'severance'] as const;
export type SalaryEntryKind = (typeof SALARY_ENTRY_KINDS)[number];
/**
 * Entries entered for a month (V72); the advance and the salary come from the salary rules. Unpaid
 * leave («за свой счёт», V145) pays nothing and takes its workdays off the salary. «Денежная помощь
 * молодому специалисту» (`aid`, V287) is a typed amount with no income tax. «Выходное пособие»
 * (`severance`, V292) pays its workdays at the average of the two months before, free of every tax.
 */
export const SALARY_EVENT_KINDS = ['vacation', 'unpaid', 'sick', 'bonus', 'compensation', 'aid', 'severance'] as const;
export type SalaryEventKind = (typeof SALARY_EVENT_KINDS)[number];
/** Events that are a typed amount alone: no dates, no days, the labour rules compute nothing (V72, V287). */
export const TYPED_EVENT_KINDS: readonly SalaryEntryKind[] = ['bonus', 'aid'];
export const isTypedEventKind = (kind: SalaryEntryKind): boolean => TYPED_EVENT_KINDS.includes(kind);

/** One payment of a salary month through an entity (V71, V72). */
export interface PayPlanSalaryEntry {
  readonly kind: SalaryEntryKind;
  /** The stored row; null while a regular entry is computed. */
  readonly itemId: string | null;
  /** The salary rule's project for a regular entry (its key), the event's own project otherwise. */
  readonly ruleProjectId: string | null;
  readonly projectName: string | null;
  /** On hand; null when it cannot be computed (no earnings to average, no amount). */
  readonly netMinor: number | null;
  /** Every tax on top and withheld (V67); null without the entity's rates. */
  readonly taxMinor: number | null;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
  readonly days: number | null;
  readonly approved: boolean;
  readonly typed: boolean;
}

/** A project's part of the salary (V145), in the salary's currency. */
/** A project the salary went to (V145, V210): a sub row of the salary on «План выплат». */
export interface PayPlanSalaryProject {
  readonly projectId: string;
  readonly projectName: string;
  readonly creditMinor: number;
}

/** The salary over the projects (V145): what they took and what is left. */
export interface PayPlanSalaryCredit {
  readonly distributedMinor: number;
  readonly remainderMinor: number;
  readonly projects: readonly PayPlanSalaryProject[];
  /** The projects' pay met the salary at a rate that is not final yet (V197): the split is approximate. */
  readonly estimate: FxEstimate | null;
}

/** A salary month of a person through one entity in one currency. */
export interface PayPlanSalaryPerson {
  readonly personId: string;
  readonly name: string;
  readonly entityId: string;
  readonly entityName: string;
  readonly currency: string;
  /** The entity has labour rules (`BY`): vacation and sick pay are computed. */
  readonly hasLaborRules: boolean;
  readonly entries: readonly PayPlanSalaryEntry[];
  /** null before the person's credit starts (V145). */
  readonly credit: PayPlanSalaryCredit | null;
  /** «Не отработано, дн.» (V150): workdays of unpaid leave this month; 0 when none. */
  readonly unpaidDays: number;
}

/** One way a project is paid (V222): through an entity's salary (`via` its name) or directly (`via` null). */
export interface PayPlanPart {
  readonly via: string | null;
  readonly currency: string;
  readonly totalMinor: number;
  /** «Итого, USD» (V227) at the month's rate; null without a rate. */
  readonly totalUsdMinor: number | null;
  /** The part is at a rate that is not final yet (V197): «≈», grey, titled with the rate used. */
  readonly estimate: FxEstimate | null;
}

/**
 * A project of a person on the one table of «План выплат» (V222): once per assignment, whatever the
 * pay format; «Ставка» is per hour for every format, «Итого» what the project earns this month.
 */
export interface PayPlanProject extends PayPlanLine {
  /** The entity it is paid through when it is paid one way only; null directly or when split. */
  readonly via: string | null;
  /** Per hour (× 10 000): the rate, a monthly fee over the month's plan hours, or «Итого» over the hours. */
  readonly rateE4: number | null;
  /** «В час, USD» and «Итого, USD» (V227) at the month's rate; null without a rate or an amount. */
  readonly rateUsdE4: number | null;
  readonly totalUsdMinor: number | null;
  /**
   * The same person's same project the month before (V228), for the change after «В час, USD» and
   * «Итого, USD»; null when it had no row then.
   */
  readonly previousUsd: { readonly rateE4: number | null; readonly totalMinor: number | null } | null;
  /** Paid two or more ways: each part with its «Итого»; empty otherwise. */
  readonly parts: readonly PayPlanPart[];
  /** The USD columns — and «Итого» of a project paid through a salary — are at a rate that is not final yet (V197). */
  readonly estimate: FxEstimate | null;
}

/** A salary's part that no project took (V218, «Без проекта», yellow), or the whole salary before its credit starts. */
export interface PayPlanSalaryRest {
  readonly entityId: string;
  readonly via: string;
  readonly currency: string;
  readonly totalMinor: number;
  readonly totalUsdMinor: number | null;
  readonly isUndistributed: boolean;
  /** What the projects took was converted at a rate that is not final yet (V197): the rest is approximate. */
  readonly estimate: FxEstimate | null;
}

/** A person on the one table of «План выплат» (V222). */
export interface PayPlanRow {
  readonly personId: string;
  readonly name: string;
  /** Σ «Итого» of the projects, each in its own currency, and «Без проекта» (V224); hours stay on the projects (V223). */
  readonly totals: readonly PayPlanMoney[];
  /** «Итого, USD» (V227): the same sum in USD at the month's rate; null when an amount has no rate. */
  readonly totalUsdMinor: number | null;
  /** «Итого» and «Итого, USD» of the person rest on a rate that is not final yet (V197). */
  readonly estimate: FxEstimate | null;
  readonly projects: readonly PayPlanProject[];
  readonly rests: readonly PayPlanSalaryRest[];
  /** ✓ of the person: what is still open — payouts by currency, salary months, their events; `done` when nothing is. */
  readonly approve: {
    readonly currencies: readonly string[];
    readonly salaries: readonly SalaryMonthKey[];
    readonly events: readonly SalaryEntryKey[];
    readonly done: boolean;
  };
}

export interface PayPlanResponse {
  readonly period: string;
  /** The one table of the screen (V222): a person, their projects, the salary they are paid through. */
  readonly people: readonly PayPlanRow[];
  /** Only non-empty tables, in the design's order. */
  readonly tables: readonly PayPlanTable[];
  readonly salaries: readonly PayPlanSalaryPerson[];
}

/** Which salary entry an approval, an amount or a deletion is for. */
export const salaryEntryKeySchema = z.object({
  personId: z.string().min(1),
  entityId: z.string().min(1),
  currency: z.string().min(1),
  period: payPlanPeriodSchema,
  kind: z.enum(SALARY_ENTRY_KINDS),
  ruleProjectId: z.string().min(1).nullable(),
  itemId: z.string().min(1).nullable(),
});
export type SalaryEntryKey = z.infer<typeof salaryEntryKeySchema>;

/** A salary month: person × entity × currency × month (V150). */
export const salaryMonthKeySchema = z.object({
  personId: z.string().min(1),
  entityId: z.string().min(1),
  currency: z.string().min(1),
  period: payPlanPeriodSchema,
});
export type SalaryMonthKey = z.infer<typeof salaryMonthKeySchema>;

/** A month has at most 23 workdays. */
const MAX_WORKDAYS = 23;

/** PUT /payplan/salary/unpaid-days — workdays not worked and not paid («за свой счёт», V150); 0 clears. */
export const salaryUnpaidDaysSchema = salaryMonthKeySchema.extend({
  days: z.number().int().min(0, 'Дней — число от 0').max(MAX_WORKDAYS, `Не больше ${MAX_WORKDAYS} рабочих дней`),
});
export type SalaryUnpaidDaysBody = z.infer<typeof salaryUnpaidDaysSchema>;

/** PUT /payplan/salary/amount — a typed amount on hand; null goes back to the computed one. */
export const salaryAmountSchema = salaryEntryKeySchema.extend({
  amountMinor: z.number().int().min(0).nullable(),
});
export type SalaryAmountBody = z.infer<typeof salaryAmountSchema>;

const isoDate = z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД');
const MAX_EVENT_DAYS = 366;

/** POST /payplan/salary/events — vacation, unpaid leave, sick leave, a bonus, the compensation for unused vacation or the aid to a молодой специалист. */
export const salaryEventSchema = z
  .object({
    personId: z.string().min(1),
    entityId: z.string().min(1),
    currency: z.string().min(1),
    period: payPlanPeriodSchema,
    kind: z.enum(SALARY_EVENT_KINDS),
    projectId: z.string().min(1).nullable().default(null),
    startsOn: isoDate.nullable().default(null),
    endsOn: isoDate.nullable().default(null),
    days: z.number().int().min(1).max(MAX_EVENT_DAYS).nullable().default(null),
    amountMinor: z.number().int().min(0).nullable().default(null),
  })
  .refine((b) => !isTypedEventKind(b.kind) || b.amountMinor != null, { message: 'Укажите сумму', path: ['amountMinor'] })
  .refine((b) => b.kind !== 'unpaid' || b.amountMinor == null, { message: 'Отпуск за свой счёт не оплачивается', path: ['amountMinor'] })
  .refine((b) => isTypedEventKind(b.kind) || b.days != null || (b.startsOn != null && b.endsOn != null), {
    message: 'Укажите даты или число дней',
    path: ['days'],
  })
  .refine((b) => !b.startsOn || !b.endsOn || b.startsOn <= b.endsOn, { message: 'Дата окончания раньше начала', path: ['endsOn'] });
export type SalaryEventBody = z.input<typeof salaryEventSchema>;
export type SalaryEvent = z.output<typeof salaryEventSchema>;

/** `minutes` for hourly and monthly lines (V96), `amount` for a monthly override or a scope stage. */
export const PAYPLAN_FIELDS = ['minutes', 'amount'] as const;
export type PayPlanField = (typeof PAYPLAN_FIELDS)[number];

/** PUT /payplan/assignments/:assignmentId/:period; `offMinutes` — «Не отработано, ч» (V306), null clears them. */
export const payPlanUpdateSchema = z.union([
  z.object({ minutes: z.number().int().min(0).max(PAYPLAN_MAX_MINUTES) }).strict(),
  z.object({ amountMinor: z.number().int().min(0) }).strict(),
  z.object({ offMinutes: z.number().int().min(0).max(OFF_MAX_MINUTES).nullable() }).strict(),
  z.object({ clear: z.enum(PAYPLAN_FIELDS) }).strict(),
]);
export type PayPlanUpdate = z.infer<typeof payPlanUpdateSchema>;

/** POST /payplan/paste — a pasted column, applied atomically; `value` in stored units. */
export const payPlanPasteSchema = z.object({
  cells: z
    .array(
      z.object({
        assignmentId: z.string().min(1),
        period: payPlanPeriodSchema,
        field: z.enum(PAYPLAN_FIELDS),
        value: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(500),
});
export type PayPlanPaste = z.infer<typeof payPlanPasteSchema>;

export interface PayPlanWriteResult {
  readonly message: string;
  readonly undoToken: string | null;
}
